<script lang="ts">
  import { getContext } from 'svelte';
  import type { Readable } from 'svelte/store';
  import { Handle, Position, type NodeProps } from '@xyflow/svelte';
  import { type NodeType } from '../../lib/automations-api';
  import { nodeCatalog } from '../../lib/node-catalog-store';
  import Icon from '../Icon.svelte';

  /** Статус ноди у поточному тест-пробігу (живиться SSE через context). */
  type RunStatus = 'running' | 'succeeded' | 'failed';

  type NodeData = {
    nodeType: NodeType;
    label?: string;
    summary?: string;
    selected?: boolean;
  };

  let { id, data, selected }: NodeProps & { data: NodeData } = $props();

  /** Подвійний клік → відкрити налаштування ноди (NDV). */
  const openNode = getContext<((nodeId: string) => void) | undefined>('automation:openNode');
  /** «+» на виході → відкрити палітру і прив'язати нову ноду до цього виходу. */
  const addNext = getContext<((sourceId: string, handle: string) => void) | undefined>('automation:addNext');
  /** Множина "source:handle" з вихідними ребрами (щоб ховати «+»). */
  const outgoingStore = getContext<Readable<Set<string>> | undefined>('automation:outgoing');
  /** Чи зараз режим редагування (показуємо «+» лише там). */
  const editingStore = getContext<Readable<boolean> | undefined>('automation:editing');

  const def = $derived($nodeCatalog[data.nodeType]);

  /** Чи показувати «+» для виходу (edit + немає вихідного ребра). */
  function showAddNext(handleId: string): boolean {
    return !!addNext && !!editingStore && !!($editingStore) && !($outgoingStore?.has(`${id}:${handleId}`));
  }

  /** Map<nodeId, RunStatus> з редактора (через SSE тест-пробігу). */
  const runStatusStore = getContext<Readable<Record<string, RunStatus>> | undefined>(
    'automation:nodeStatus',
  );
  const runStatus = $derived(runStatusStore ? ($runStatusStore?.[id] as RunStatus | undefined) : undefined);

  const statusRing = $derived.by(() => {
    switch (runStatus) {
      case 'running':
        return 'ring-2 ring-amber-400 ring-offset-1 animate-pulse';
      case 'succeeded':
        return 'ring-2 ring-emerald-400 ring-offset-1';
      case 'failed':
        return 'ring-2 ring-rose-500 ring-offset-1';
      default:
        return '';
    }
  });

  const colorClasses = $derived.by(() => {
    const c = def?.color ?? 'slate';
    const map: Record<string, { bg: string; border: string; icon: string; ring: string }> = {
      blue:    { bg: 'bg-blue-50',    border: 'border-blue-300',    icon: 'bg-blue-100 text-blue-700',       ring: 'ring-blue-400' },
      purple:  { bg: 'bg-purple-50',  border: 'border-purple-300',  icon: 'bg-purple-100 text-purple-700',   ring: 'ring-purple-400' },
      emerald: { bg: 'bg-emerald-50', border: 'border-emerald-300', icon: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-400' },
      amber:   { bg: 'bg-amber-50',   border: 'border-amber-300',   icon: 'bg-amber-100 text-amber-700',     ring: 'ring-amber-400' },
      slate:   { bg: 'bg-slate-50',   border: 'border-slate-300',   icon: 'bg-slate-100 text-slate-700',     ring: 'ring-slate-400' },
    };
    return map[c] ?? map.slate!;
  });
</script>

{#if def}
  <div
    class="group relative rounded-xl border-2 bg-white shadow-sm w-[220px] {colorClasses.bg} {colorClasses.border} {runStatus ? statusRing : selected ? `ring-2 ${colorClasses.ring}` : ''}"
    ondblclick={() => openNode?.(id)}
    role="presentation"
  >
    {#if runStatus}
      <div
        class="absolute -top-2 -left-2 grid place-items-center w-5 h-5 rounded-full shadow-sm z-10 text-white {runStatus === 'running' ? 'bg-amber-500' : runStatus === 'succeeded' ? 'bg-emerald-500' : 'bg-rose-500'}"
        title={runStatus === 'running' ? 'Виконується…' : runStatus === 'succeeded' ? 'Успішно' : 'Помилка'}
      >
        <Icon
          name={runStatus === 'running' ? 'refresh' : runStatus === 'succeeded' ? 'check' : 'close'}
          size={11}
        />
      </div>
    {/if}
    <!-- Видалення — через контекстне меню (ПКМ) -->
    <!-- Inputs (зверху) -->
    {#each def.inputs as input (input.id)}
      <Handle
        type="target"
        position={Position.Top}
        id={input.id}
        style="background:#94a3b8; width:10px; height:10px; border:2px solid white;"
      />
    {/each}

    <!-- Header -->
    <div class="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border-3)]">
      <div class="grid place-items-center w-7 h-7 rounded-lg {colorClasses.icon}">
        <Icon name={def.icon} size={16} />
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-bold truncate">
          {def.label}
        </div>
        <div class="text-xs font-semibold truncate">{data.label ?? def.label}</div>
      </div>
    </div>

    <!-- Body: short summary -->
    {#if data.summary}
      <div class="px-3 py-2 text-[10px] text-[var(--color-text-muted)] font-mono break-all line-clamp-3">
        {data.summary}
      </div>
    {:else}
      <div class="px-3 py-2 text-[10px] italic text-[var(--color-text-muted)]">
        {def.description}
      </div>
    {/if}

    <!-- Outputs (знизу) — з підписами для multi-output (наприклад IF) -->
    {#if def.outputs.length > 1}
      <div class="flex justify-around border-t border-[var(--color-border-3)] py-1 text-[9px] uppercase tracking-wider text-[var(--color-text-muted)] font-bold">
        {#each def.outputs as out (out.id)}
          <div class="relative">
            <span>{out.label ?? out.id}</span>
            <Handle
              type="source"
              position={Position.Bottom}
              id={out.id}
              style="background:#94a3b8; width:10px; height:10px; border:2px solid white; left:50%;"
            />
            {#if showAddNext(out.id)}
              <div class="absolute left-1/2 -translate-x-1/2 top-full flex flex-col items-center pointer-events-none">
                <div class="w-px h-3 bg-[var(--color-border-3)]"></div>
                <button
                  class="pointer-events-auto grid place-items-center w-5 h-5 rounded bg-white border border-[var(--color-border-3)] text-[var(--color-pu-3)] shadow hover:bg-purple-50 hover:border-[var(--color-pu-2)] transition-colors"
                  onclick={(e) => { e.stopPropagation(); addNext?.(id, out.id); }}
                  title="Додати наступну ноду"
                ><Icon name="add" size={12} /></button>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {:else if def.outputs.length === 1}
      {@const outId = def.outputs[0]!.id}
      {#if showAddNext(outId)}
        <!-- «+» = вихідний хендл: тягнути = з'єднати, клік = відкрити палітру -->
        <div class="absolute left-1/2 -translate-x-1/2 top-full w-px h-6 bg-[var(--color-border-3)] pointer-events-none"></div>
        <Handle
          type="source"
          position={Position.Bottom}
          id={outId}
          onclick={() => addNext?.(id, outId)}
          class="!w-6 !h-6 !min-w-0 !min-h-0 !rounded-md !bg-white !border !border-[var(--color-border-3)] !grid !place-items-center !text-[var(--color-pu-3)] shadow hover:!bg-purple-50 hover:!border-[var(--color-pu-2)] cursor-pointer"
          style="top: calc(100% + 24px); bottom: auto; transform: translateX(-50%);"
          title="Тягни щоб з'єднати · клік щоб додати ноду"
        ><Icon name="add" size={14} /></Handle>
      {:else}
        <Handle
          type="source"
          position={Position.Bottom}
          id={outId}
          style="background:#94a3b8; width:10px; height:10px; border:2px solid white;"
        />
      {/if}
    {/if}
  </div>
{:else}
  <div class="rounded-lg border border-rose-300 bg-rose-50 p-2 text-xs">
    Невідомий тип: {data.nodeType}
  </div>
{/if}
